import type { AiFieldGenerationWriteContext } from '@databases/ai-first-creation-token-processing';
import {
  type AiSummaryInteraction,
  beginAiSummaryInteraction,
  getDatabaseContainerType,
  type AiFieldRunScope,
  type AiFieldRunSource,
  type AiFieldRunTriggerType,
  useAnalytics as getAnalyticsClient
} from '@databases/analytics';
import { ROW_RENDER_DELAY_MS } from '@databases/database-ai-first-creation-state';
import { logError } from '@databases/logs';
import { runDatabaseAiField } from '@databases/rovo-bridge';
import type { AiSummaryFieldConfig } from '@databases/types/database';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import {
  hasNoEmptyAiSummaryEntries,
  prepareAiFieldGeneration,
  prepareAiFieldRefresh
} from '../prepare-manual-ai-field-refresh.ts';
import {
  runManualAiFieldRefresh,
  type ManualAiFieldRefreshAdapter
} from '../run-manual-ai-field-refresh.ts';
import { aiSummarySettlementStateManager } from './ai-summary-settlement-state.ts';

import {
  type AiSummaryFieldRunOperation,
  buildAiSummaryPrompt
} from './ai-summary-prompts.ts';

export { hasUserEditedAiSummaryCells } from './has-user-edited-ai-summary-cells.ts';
export { hasNoEmptyAiSummaryEntries };

export type AiSummaryRunResult =
  | { kind: 'generated' }
  | { kind: 'feature-disabled' }
  | { kind: 'no-eligible-entries' }
  | { kind: 'no-committed-output' }
  | { kind: 'overlapping-run' };

type RunAiSummaryFieldBaseOptions = {
  abortSignal?: AbortSignal;
  contentId: string;
  fieldId: string;
  targetFieldName: string;
  config: AiSummaryFieldConfig;
  locale: string;
  viewId: string;
  triggerType: AiFieldRunTriggerType;
  runScope: AiFieldRunScope;
  source: AiFieldRunSource;
};

type RunAiSummaryFieldAnalyticsOptions = RunAiSummaryFieldBaseOptions & {
  operation: AiSummaryFieldRunOperation;
};

export type RunAiSummaryFieldOptions = RunAiSummaryFieldBaseOptions &
  (
    | {
        operation: Exclude<
          AiSummaryFieldRunOperation,
          'single-entry-generate' | 'multiple-entry-generate'
        >;
      }
    | {
        operation: 'single-entry-generate';
        entryId: string;
      }
  );

export type RunManualAiSummaryRefreshOptions = RunAiSummaryFieldBaseOptions & {
  viewId: string;
} & (
    | {
        operation: 'refresh-entire-column' | 'refresh-empty-entries';
      }
    | {
        operation: 'single-entry-generate';
        entryId: string;
      }
    | {
        operation: 'multiple-entry-generate';
        entryIds: readonly string[];
      }
  );

const isAiSummaryEnabled = (): boolean =>
  getExperimentValue('databasesFrontendAiFieldsSummary', 'enabled', {
    fireExperimentExposure: false
  });

const logAndRethrow = (error: unknown): never => {
  logError(
    'Failed to generate AI Summary field',
    error instanceof Error ? error : new Error(String(error))
  );
  throw error;
};

const waitForSettlementFeedback = async (): Promise<void> => {
  await new Promise<void>((resolve) => {
    globalThis.setTimeout(resolve, ROW_RENDER_DELAY_MS);
  });
};

const runPreparedAiSummaryEntries = async ({
  abortSignal,
  eligibleEntryIds,
  fieldId,
  generationWriteContext,
  onGeneratedValueCommitted,
  runLlmEntries
}: {
  abortSignal?: AbortSignal;
  eligibleEntryIds: readonly string[];
  fieldId: string;
  generationWriteContext: AiSummaryGenerationWriteContext;
  onGeneratedValueCommitted: (entryId: string) => void;
  runLlmEntries: (
    entryIds: readonly string[],
    writeContext: AiFieldGenerationWriteContext
  ) => Promise<void>;
}): Promise<void> => {
  const noContentEntryIds = eligibleEntryIds.filter(
    (entryId) => generationWriteContext.getSourceContentState(entryId) === 'empty'
  );
  const noContentEntryIdSet = new Set(noContentEntryIds);
  const llmEntryIds = eligibleEntryIds.filter(
    (entryId) => !noContentEntryIdSet.has(entryId)
  );
  const receivedEntryIds = new Set<string>();

  const settlementRun = aiSummarySettlementStateManager.begin(fieldId, eligibleEntryIds);
  aiSummarySettlementStateManager.markPending(settlementRun, noContentEntryIds);

  try {
    await Promise.all([
      noContentEntryIds.length > 0 ? waitForSettlementFeedback() : Promise.resolve(),
      llmEntryIds.length > 0
        ? runLlmEntries(llmEntryIds, {
            ...generationWriteContext,
            onGeneratedValueReceived: (entryId) => {
              receivedEntryIds.add(entryId);
            },
            onGeneratedValueCommitted
          })
        : Promise.resolve()
    ]);

    abortSignal?.throwIfAborted();
    const noResultEntryIds = llmEntryIds.filter(
      (entryId) => !receivedEntryIds.has(entryId)
    );
    const emptyOutcomeEntryIds = [...noContentEntryIds, ...noResultEntryIds];
    aiSummarySettlementStateManager.settle(settlementRun, emptyOutcomeEntryIds);

    if (emptyOutcomeEntryIds.length > 0) {
      await waitForSettlementFeedback();
      abortSignal?.throwIfAborted();
      for (const entryId of noContentEntryIds) {
        generationWriteContext.commitEmptyOutcome(entryId, 'no-content');
      }
      for (const entryId of noResultEntryIds) {
        generationWriteContext.commitEmptyOutcome(entryId, 'no-result');
      }
    }
    aiSummarySettlementStateManager.finish(settlementRun, emptyOutcomeEntryIds);
  } catch (error) {
    aiSummarySettlementStateManager.cancel(settlementRun);
    throw error;
  }
};

const runWithAnalytics = async (
  options: RunAiSummaryFieldAnalyticsOptions,
  run: (traceContext: AiSummaryInteraction['traceContext']) => Promise<AiSummaryRunResult>
): Promise<AiSummaryRunResult> => {
  const analytics = getAnalyticsClient();
  const interaction = beginAiSummaryInteraction(
    options.contentId,
    `field:${options.fieldId}`,
    options.source
  );
  const eventData = {
    contentId: options.contentId,
    databaseContainer: getDatabaseContainerType(),
    fieldId: options.fieldId,
    aiFieldType: 'summary' as const,
    runId: globalThis.crypto.randomUUID(),
    triggerType: options.triggerType,
    operation: options.operation,
    runScope: options.runScope,
    source: options.source
  };

  void analytics.sendAiFieldRunStartedEvent(eventData);

  try {
    const result = await run(interaction.traceContext);
    if (result.kind === 'generated') {
      void analytics.sendAiFieldRunCompletedEvent(eventData);
      interaction.resultViewed();
    } else {
      interaction.endResultView();
    }
    return result;
  } catch (error) {
    interaction.endResultView();
    if (
      options.abortSignal?.aborted === true &&
      (error === options.abortSignal.reason ||
        (error instanceof Error && error.name === 'AbortError'))
    ) {
      throw error;
    }
    const errorCode =
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      typeof error.code === 'string'
        ? error.code
        : undefined;
    void analytics.sendAiFieldRunFailedEvent({
      ...eventData,
      errorCategory: error instanceof Error ? error.name : 'unknown',
      errorCode
    });
    return logAndRethrow(error);
  }
};

export function runAiSummaryField(
  options: RunAiSummaryFieldOptions
): Promise<AiSummaryRunResult> {
  if (!isAiSummaryEnabled()) {
    return Promise.resolve({ kind: 'feature-disabled' });
  }

  return runWithAnalytics(options, async (traceContext) => {
    options.abortSignal?.throwIfAborted();
    let didPersistGeneratedSummary = false;
    const scope =
      options.operation === 'single-entry-generate'
        ? ({ kind: 'entry', entryId: options.entryId } as const)
        : options.operation === 'refresh-empty-entries'
          ? ({ kind: 'empty-target-field-entries' } as const)
          : ({ kind: 'all-entries' } as const);
    const preparation = await prepareAiFieldGeneration(
      options.contentId,
      options.fieldId,
      scope,
      { viewId: options.viewId, config: options.config }
    );
    options.abortSignal?.throwIfAborted();
    if (preparation.eligibleEntryIds.length === 0) {
      return { kind: 'no-eligible-entries' };
    }
    await runPreparedAiSummaryEntries({
      ...(options.abortSignal !== undefined && { abortSignal: options.abortSignal }),
      eligibleEntryIds: preparation.eligibleEntryIds,
      fieldId: options.fieldId,
      generationWriteContext: preparation.generationWriteContext,
      onGeneratedValueCommitted: () => {
        didPersistGeneratedSummary = true;
      },
      runLlmEntries: async (entryIds, generationWriteContext) => {
        const request = {
          ...(options.abortSignal !== undefined && {
            abortSignal: options.abortSignal
          }),
          contentId: options.contentId,
          entryFieldValuesSnapshot: preparation.requestContext,
          generationWriteContext,
          locale: options.locale,
          traceContext,
          prompt: buildAiSummaryPrompt({
            operation: options.operation,
            targetFieldName: options.targetFieldName,
            config: options.config
          })
        } as const;

        if (options.operation === 'single-entry-generate') {
          await runDatabaseAiField({
            ...request,
            target: {
              aiFieldType: 'summary',
              fieldId: options.fieldId,
              scope: 'entry',
              entryId: options.entryId
            }
          });
          return;
        }
        await runDatabaseAiField({
          ...request,
          contextScope: { kind: 'entry-ids', entryIds },
          target: {
            aiFieldType: 'summary',
            fieldId: options.fieldId,
            scope: 'field'
          }
        });
      }
    });
    return didPersistGeneratedSummary
      ? { kind: 'generated' }
      : { kind: 'no-committed-output' };
  });
}

type AiSummaryGenerationWriteContext = Awaited<
  ReturnType<typeof prepareAiFieldRefresh>
>['generationWriteContext'];
type AiSummaryRequestContext = Awaited<
  ReturnType<typeof prepareAiFieldRefresh>
>['requestContext'];

const createAiSummaryManualRefreshAdapter = (
  traceContext: AiSummaryInteraction['traceContext'],
  onGeneratedValueCommitted: () => void
): ManualAiFieldRefreshAdapter<
  RunManualAiSummaryRefreshOptions,
  AiSummaryGenerationWriteContext,
  AiSummaryRequestContext
> => ({
  aiFieldType: 'summary',
  getAwarenessScope: (options) =>
    options.operation === 'single-entry-generate'
      ? { kind: 'entry', entryId: options.entryId }
      : options.operation === 'multiple-entry-generate'
        ? { kind: 'entries', entryIds: options.entryIds }
        : {
            kind: 'field',
            operation:
              options.operation === 'refresh-empty-entries'
                ? 'empty-target-entries'
                : 'all-entries'
          },
  prepare: (options) => {
    const scope =
      options.operation === 'single-entry-generate'
        ? ({ kind: 'entry', entryId: options.entryId } as const)
        : options.operation === 'multiple-entry-generate'
          ? ({ kind: 'entries', entryIds: options.entryIds } as const)
          : options.operation === 'refresh-empty-entries'
            ? ({ kind: 'empty-target-field-entries' } as const)
            : ({ kind: 'all-entries' } as const);

    return prepareAiFieldRefresh(options.contentId, options.fieldId, scope, {
      viewId: options.viewId,
      config: options.config
    });
  },
  runEligibleEntries: async (
    options,
    eligibleEntryIds,
    generationWriteContext,
    requestContext
  ) => {
    await runPreparedAiSummaryEntries({
      ...(options.abortSignal !== undefined && { abortSignal: options.abortSignal }),
      eligibleEntryIds,
      fieldId: options.fieldId,
      generationWriteContext,
      onGeneratedValueCommitted: () => onGeneratedValueCommitted(),
      runLlmEntries: async (entryIds, llmGenerationWriteContext) => {
        const request = {
          ...(options.abortSignal !== undefined && {
            abortSignal: options.abortSignal
          }),
          contentId: options.contentId,
          entryFieldValuesSnapshot: requestContext,
          generationWriteContext: llmGenerationWriteContext,
          locale: options.locale,
          traceContext,
          prompt: buildAiSummaryPrompt({
            operation: options.operation,
            targetFieldName: options.targetFieldName,
            config: options.config
          })
        } as const;

        if (options.operation === 'single-entry-generate') {
          await runDatabaseAiField({
            ...request,
            target: {
              aiFieldType: 'summary',
              fieldId: options.fieldId,
              scope: 'entry',
              entryId: options.entryId
            }
          });
          return;
        }

        if (options.operation === 'multiple-entry-generate') {
          await runDatabaseAiField({
            ...request,
            target: {
              aiFieldType: 'summary',
              fieldId: options.fieldId,
              scope: 'entries',
              entryIds
            }
          });
          return;
        }

        await runDatabaseAiField({
          ...request,
          contextScope: { kind: 'entry-ids', entryIds },
          target: {
            aiFieldType: 'summary',
            fieldId: options.fieldId,
            scope: 'field'
          }
        });
      }
    });
  }
});

export function runManualAiSummaryRefresh(
  options: RunManualAiSummaryRefreshOptions
): Promise<AiSummaryRunResult> {
  if (!isAiSummaryEnabled()) {
    return Promise.resolve({ kind: 'feature-disabled' });
  }

  return runWithAnalytics(options, async (traceContext) => {
    let didPersistGeneratedSummary = false;
    const result = await runManualAiFieldRefresh(
      options,
      createAiSummaryManualRefreshAdapter(traceContext, () => {
        didPersistGeneratedSummary = true;
      })
    );
    if (result.kind !== 'ran') {
      return result;
    }
    return didPersistGeneratedSummary
      ? { kind: 'generated' }
      : { kind: 'no-committed-output' };
  });
}
